import { useAuthenticationContext } from '@festina-life/authentication'
import { IconButton } from '@festina-life/ui/actions'
import { zIndexStyles } from '@festina-life/ui/libs'
import cx from 'classnames'
import { lazy, type PropsWithChildren, Suspense, useState } from 'react'

import { Settings } from '../settings/Settings'
import { LabelWithWarning } from './LabelWithWarning'
import { TimeSettings } from './TimeSettings'

type Props = PropsWithChildren<{
  shouldAllowToChangeTime: boolean
  shouldShowTimeSettings?: boolean
  className?: string
}>

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/production').then((d) => ({
    default: d.ReactQueryDevtools,
  })),
)

export const DevTools = ({
  children,
  shouldAllowToChangeTime,
  shouldShowTimeSettings = true,
  className,
}: Props) => {
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const { environmentSettings, onChangeEnvironmentSettings, api } =
    useAuthenticationContext()

  const [shouldLoadDevtools, setShouldLoadDevtools] = useState(false)

  return (
    <>
      <div
        className={cx(
          'fixed bottom-0 right-0 flex items-center rounded-tl bg-amaze-paper/50 font-code text-sm leading-none dark:bg-amaze-paperDark/50',
          zIndexStyles('bars'),
          '[&_.tsqd-open-btn]:size-3!',
          className,
        )}
      >
        {shouldLoadDevtools ? (
          <Suspense fallback={null}>
            <ReactQueryDevtoolsProduction
              buttonPosition="relative"
              initialIsOpen
            />
          </Suspense>
        ) : (
          <IconButton
            iconName="CommandLine"
            onClick={() => setShouldLoadDevtools(true)}
          />
        )}
        <LabelWithWarning
          hasWarning={api !== 'localhost'}
          onClick={() => setIsSettingsModalOpen(true)}
        >
          {api}
        </LabelWithWarning>
        {shouldShowTimeSettings && (
          <TimeSettings shouldAllowToChangeTime={shouldAllowToChangeTime} />
        )}
        {children}
      </div>
      <Settings
        value={environmentSettings ? { ...environmentSettings, api } : null}
        onChange={onChangeEnvironmentSettings}
        isModalOpen={isSettingsModalOpen}
        onCloseModal={
          () => window.location.reload() // we can't use useNavigate here because it's not a child of a Router
        }
      />
    </>
  )
}
